import type * as Feather from "react-feather";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart2,
  Box,
  Calendar,
  Check,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Code,
  Copy,
  Download,
  Edit,
  Edit2,
  FileText,
  Grid,
  Home,
  Info,
  Layout,
  Link,
  Loader,
  LogOut,
  Map,
  Menu,
  Minus,
  MinusCircle,
  MoreVertical,
  Package,
  Plus,
  Printer,
  Save,
  Search,
  Server,
  Settings,
  Sliders,
  Square,
  Trash,
  Truck,
  UploadCloud,
  User,
  Users,
  X,
  XCircle,
  XOctagon,
  Zap,
} from "react-feather";

/**
 * The icons pallet draws, curated *from* react-feather's own export type.
 *
 * `Pick`, not `Extract`: `Extract<keyof typeof Feather, "Chekc">` silently
 * evaluates to `never`, while `Pick<typeof Feather, "Chekc">` is a compile
 * error. The names are therefore checked against the library rather than
 * transcribed from it.
 *
 * This list is deliberately curated, not generated — adding an icon is meant
 * to be a one-line edit here plus a one-line edit in `ICONS`.
 */
type PalletIcons = Pick<
  typeof Feather,
  | "AlertCircle"
  | "AlertTriangle"
  | "ArrowLeft"
  | "ArrowRight"
  | "BarChart2"
  | "Box"
  | "Calendar"
  | "Check"
  | "CheckCircle"
  | "CheckSquare"
  | "ChevronDown"
  | "ChevronLeft"
  | "ChevronRight"
  | "ChevronUp"
  | "Code"
  | "Copy"
  | "Download"
  | "Edit"
  | "Edit2"
  | "FileText"
  | "Grid"
  | "Home"
  | "Info"
  | "Layout"
  | "Link"
  | "Loader"
  | "LogOut"
  | "Map"
  | "Menu"
  | "Minus"
  | "MinusCircle"
  | "MoreVertical"
  | "Package"
  | "Plus"
  | "Printer"
  | "Save"
  | "Search"
  | "Server"
  | "Settings"
  | "Sliders"
  | "Square"
  | "Trash"
  | "Truck"
  | "UploadCloud"
  | "User"
  | "Users"
  | "X"
  | "XCircle"
  | "XOctagon"
  | "Zap"
>;

/** Every icon pallet can draw. react-feather's own PascalCase names. */
export type IconName = keyof PalletIcons;

/**
 * The icon size ramp, passed straight through to react-feather's own `size`
 * prop (which sets both `width` and `height`).
 *
 * Not a free number: a call site that wants something off the ramp is a
 * conversation about the ramp, not a one-off.
 */
export type IconSize = 16 | 18 | 24;

/**
 * The name → component map.
 *
 * Typed as `PalletIcons`, so it is exhaustive in both directions: a name in
 * the `Pick` with no entry here is a compile error, and an entry here that is
 * not in the `Pick` is too. Static named imports keep it tree-shakeable.
 */
const ICONS: PalletIcons = {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart2,
  Box,
  Calendar,
  Check,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Code,
  Copy,
  Download,
  Edit,
  Edit2,
  FileText,
  Grid,
  Home,
  Info,
  Layout,
  Link,
  Loader,
  LogOut,
  Map,
  Menu,
  Minus,
  MinusCircle,
  MoreVertical,
  Package,
  Plus,
  Printer,
  Save,
  Search,
  Server,
  Settings,
  Sliders,
  Square,
  Trash,
  Truck,
  UploadCloud,
  User,
  Users,
  X,
  XCircle,
  XOctagon,
  Zap,
};

export interface IconProps {
  /** react-feather's PascalCase name, e.g. `"MoreVertical"`. */
  name: IconName;
  /** Size on the ramp, in pixels. Defaults to `18`. */
  size?: IconSize;
  /**
   * The icon's accessible name.
   *
   * react-feather renders a bare `<svg>` with no accessible name at all, so
   * the default here is the safe one: **decorative**, `aria-hidden`, for the
   * overwhelmingly common case of an icon sitting next to the text that names
   * it. Pass `label` only when the icon *is* the label — an icon-only button,
   * a status glyph with no adjacent text — and it becomes `role="img"` with
   * that name.
   */
  label?: string;
  "data-testid"?: string;
}

/**
 * Pallet's icon.
 *
 * The prop is a **string**, not a component: nothing react-feather-shaped
 * crosses the boundary, and consumers cannot reach past this to set their own
 * sizing or classes. Colour is inherited — put a `color` (or `text-*`) on the
 * element that owns the icon and react-feather's `stroke="currentColor"`
 * follows it.
 *
 * `pallet/src/components/icon.tsx` is the only file in the outbound surface
 * that may import `react-feather` (SR-301 phase 3).
 */
function Icon({ name, size = 18, label, "data-testid": testId }: IconProps) {
  const Component = ICONS[name];

  return (
    <Component
      size={size}
      className="shrink-0"
      data-testid={testId}
      {...(label === undefined
        ? { "aria-hidden": true }
        : { role: "img", "aria-label": label })}
    />
  );
}

export default Icon;
